import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ArrowDown, ArrowUp, Calculator, Copy, Plus, Star, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { supabase } from "@/integrations/supabase/client";
import { AppShell } from "@/components/AppShell";
import { FeatureGate } from "@/components/UpgradeGate";
import { CollapsibleSection } from "@/components/CollapsibleSection";
import { StartHereCard } from "@/components/onboarding/StartHereCard";

import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { VisualCanvas } from "@/components/visuals/VisualCanvas";
import {
  KIND_GROUPS,
  KIND_LABEL,
  defaultConfig,
  normaliseGrid,
  withDefaults,
  SAMPLE_GRIDS,
  type Visual,
  type VisualKind,
} from "@/lib/visuals/types";
import { autoMap } from "@/lib/visuals/map";
import { planErrorMessage } from "@/lib/plan-errors";
import { useVisualPins } from "@/lib/visuals-pins";
import { cn } from "@/lib/utils";

const ALL_KIND_KEYS = KIND_GROUPS.flatMap((g) => g.kinds.map((k) => k.kind as string));

export const Route = createFileRoute("/_authenticated/visuals/")({
  head: () => ({
    meta: [
      { title: "Visuals studio — MyPriorities" },
      {
        name: "description",
        content:
          "Paste or upload data and turn it into charts, Gantt roadmaps, RAG and RACI frameworks or quick business calcs.",
      },
      { property: "og:title", content: "Visuals studio — MyPriorities" },
      {
        property: "og:description",
        content: "Quick, presentable visuals from pasted data, uploads or your own hub.",
      },
    ],
  }),
  component: GalleryRoute,
});

function GalleryRoute() {
  return (
    <FeatureGate
      requires="pro"
      title="Visuals studio"
      subtitle="Make difficult decisions easier — paste, upload or pull your hub data and get a presentable visual in seconds. Business case maths lives in Quick calcs."
    >
      <Gallery />
    </FeatureGate>
  );
}

type Row = {
  id: string;
  kind: VisualKind;
  title: string;
  subtitle: string | null;
  source_note: string | null;
  config: unknown;
  data: unknown;
  updated_at: string;
};

function Gallery() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const pins = useVisualPins(ALL_KIND_KEYS);

  const { data: visuals = [], isLoading } = useQuery({
    queryKey: ["visuals"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("visuals")
        .select("id, kind, title, subtitle, source_note, config, data, updated_at")
        .order("updated_at", { ascending: false });
      if (error) throw error;
      return data as Row[];
    },
  });

  const create = useMutation({
    mutationFn: async (kind: VisualKind) => {
      const { data: auth } = await supabase.auth.getUser();
      const user = auth.user;
      if (!user) throw new Error("Please sign in again.");
      const grid = SAMPLE_GRIDS[kind] ?? { headers: [], rows: [] };
      const config = autoMap(grid, kind, defaultConfig());
      const { data, error } = await supabase
        .from("visuals")
        .insert({
          user_id: user.id,
          kind,
          title: `New ${KIND_LABEL[kind].toLowerCase()}`,
          config: config as never,
          data: grid as never,
        })
        .select("id")
        .single();
      if (error) throw error;
      return data.id as string;
    },
    onSuccess: (id) => navigate({ to: "/visuals/$id", params: { id } }),
    onError: (e: Error) => toast.error(planErrorMessage(e, "Couldn't create that visual.")),
  });

  const duplicate = useMutation({
    mutationFn: async (row: Row) => {
      const { data: auth } = await supabase.auth.getUser();
      const user = auth.user;
      if (!user) throw new Error("Please sign in again.");
      const { error } = await supabase.from("visuals").insert({
        user_id: user.id,
        kind: row.kind,
        title: `${row.title} (copy)`,
        subtitle: row.subtitle,
        source_note: row.source_note,
        config: row.config as never,
        data: row.data as never,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["visuals"] });
      toast.success("Duplicated.");
    },
    onError: (e: Error) => toast.error(planErrorMessage(e, "Couldn't duplicate that visual.")),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("visuals").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["visuals"] });
      setDeleteId(null);
      toast.success("Visual deleted.");
    },
    onError: (e: Error) => toast.error(planErrorMessage(e, "Couldn't delete that visual.")),
  });

  return (
    <AppShell
      title="Visuals studio"
      subtitle="Paste, upload or pull your hub data and get a presentable visual in seconds."
    >
      <StartHereCard
        tipKey="visuals"
        title="Build the case before committing the money"
        body="Open “Start something new”, pick a business case, chart, framework or quick calc, then paste your rows into the data pane. It works from numbers you already keep — no separate deck or spreadsheet to maintain."
        actions={[{ label: "Try the business case calc", to: "/visuals/business-case" }]}
      />

      <div className="space-y-2">
        <CollapsibleSection
          storageKey="visuals-saved"
          title="Saved visuals"
          description="Your boards, charts and frameworks — open one to keep editing."
          summary={`${visuals.length} saved`}
          className="mt-0"
        >
          {isLoading ? (
            <p className="mt-4 text-sm text-muted-foreground">Loading…</p>
          ) : visuals.length === 0 ? (
            <p className="mt-4 text-sm text-muted-foreground">
              Nothing saved yet — pick a visual type in “Start something new” below.
            </p>
          ) : (
            <div className="mt-4 grid fill-track gap-4 md:grid-cols-2 xl:grid-cols-3">
              {visuals.map((v) => {
                const config = withDefaults(v.config);
                const grid = normaliseGrid(v.data);
                return (
                  <div key={v.id} className="panel group relative p-3">
                    <Link to="/visuals/$id" params={{ id: v.id }} className="block">
                      <div className="pointer-events-none h-56 overflow-hidden rounded-xl">
                        <VisualCanvas
                          kind={v.kind}
                          grid={grid}
                          config={config}
                          title={v.title}
                          subtitle={v.subtitle}
                          compact
                        />
                      </div>
                    </Link>
                    <div className="mt-2 flex items-center justify-between gap-2">
                      <span className="truncate text-xs text-muted-foreground">
                        {KIND_LABEL[v.kind]} · updated{" "}
                        {new Date(v.updated_at).toLocaleDateString("en-GB", {
                          day: "numeric",
                          month: "short",
                        })}
                      </span>
                      <span className="flex shrink-0 gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          aria-label="Duplicate"
                          onClick={() => duplicate.mutate(v)}
                          className="size-7"
                        >
                          <Copy className="size-3.5" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          aria-label="Delete"
                          onClick={() => setDeleteId(v.id)}
                          className="size-7 text-muted-foreground hover:text-destructive"
                        >
                          <Trash2 className="size-3.5" />
                        </Button>
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CollapsibleSection>

        {pins.pinned.length ? (
          <CollapsibleSection
            storageKey="visuals-pinned"
            title="Your visual types"
            description="The types you've kept to hand, in your order."
            summary={`${pins.pinned.length} kept`}
          >
            <div className="mt-3 grid fill-track gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {pins.pinned.map((key, index) => {
                const kind = key as VisualKind;
                return (
                  <div
                    key={key}
                    className="flex items-center gap-1 rounded-lg border border-border p-3"
                  >
                    <button
                      type="button"
                      disabled={create.isPending}
                      onClick={() => create.mutate(kind)}
                      className="flex-1 text-left text-sm font-medium disabled:opacity-60"
                    >
                      <span className="flex items-center gap-2">
                        <Plus className="size-3.5 text-primary" />
                        {KIND_LABEL[kind]}
                      </span>
                    </button>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Move ${KIND_LABEL[kind]} up`}
                      disabled={index === 0}
                      onClick={() => pins.move(key, -1)}
                    >
                      <ArrowUp className="size-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Move ${KIND_LABEL[kind]} down`}
                      disabled={index === pins.pinned.length - 1}
                      onClick={() => pins.move(key, 1)}
                    >
                      <ArrowDown className="size-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Remove ${KIND_LABEL[kind]} from your types`}
                      onClick={() => pins.toggle(key)}
                    >
                      <Star className="size-4 fill-current text-primary" />
                    </Button>
                  </div>
                );
              })}
            </div>
          </CollapsibleSection>
        ) : null}

        <CollapsibleSection
          storageKey="visuals-new"
          title="Start something new"
          description="Pick a type — it opens with sample data you can replace by pasting, uploading or pulling from your hub."
          summary={`${KIND_GROUPS.reduce((n, g) => n + g.kinds.length, 0)} visual types`}
        >
          <div className="mt-3 space-y-2">
            {KIND_GROUPS.map((group, i) => (
              <CollapsibleSection
                key={group.group}
                storageKey={`visuals-group-${group.group}`}
                title={group.group}
                summary={`${group.kinds.length} type${group.kinds.length === 1 ? "" : "s"}`}
                defaultOpen={i === 0}
                className="panel p-4"
              >
                <div className="mt-3 grid fill-track gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {group.kinds.map((k) => (
                    <div
                      key={k.kind}
                      className="relative rounded-lg border border-border transition-colors hover:border-primary hover:bg-accent"
                    >
                      <button
                        type="button"
                        disabled={create.isPending}
                        onClick={() => create.mutate(k.kind)}
                        className="block w-full p-3 pr-10 text-left disabled:opacity-60"
                      >
                        <span className="flex items-center gap-2 text-sm font-medium">
                          <Plus className="size-3.5 text-primary" />
                          {k.label}
                        </span>
                        <span className="mt-0.5 block text-xs text-muted-foreground">{k.hint}</span>
                      </button>
                      <button
                        type="button"
                        aria-label={
                          pins.isPinned(k.kind)
                            ? `Remove ${k.label} from your types`
                            : `Keep ${k.label} to hand`
                        }
                        onClick={() => pins.toggle(k.kind)}
                        className="absolute right-2 top-2 rounded p-1 text-muted-foreground hover:text-foreground"
                      >
                        <Star
                          className={cn(
                            "size-4",
                            pins.isPinned(k.kind) ? "fill-current text-primary" : "",
                          )}
                        />
                      </button>
                    </div>
                  ))}
                  {group.group === "Quick calcs" ? (
                    <Link
                      to="/visuals/business-case"
                      className="rounded-lg border border-border p-3 text-left transition-colors hover:border-primary hover:bg-accent"
                    >
                      <span className="flex items-center gap-2 text-sm font-medium">
                        <Calculator className="size-3.5 text-primary" />
                        Business case
                      </span>
                      <span className="mt-0.5 block text-xs text-muted-foreground">
                        Revenue, cost, margin, ARR and TCV — saved cases you can revisit
                      </span>
                    </Link>
                  ) : null}
                  {group.group === "Quick calcs" ? (
                    <Link
                      to="/visuals/price-book"
                      className="rounded-lg border border-border p-3 text-left transition-colors hover:border-primary hover:bg-accent"
                    >
                      <span className="flex items-center gap-2 text-sm font-medium">
                        <Calculator className="size-3.5 text-primary" />
                        Price book
                      </span>
                      <span className="mt-0.5 block text-xs text-muted-foreground">
                        Products, cost variables and commercial drivers that price your cases
                      </span>
                    </Link>
                  ) : null}
                </div>
              </CollapsibleSection>
            ))}
          </div>
        </CollapsibleSection>

        <AlertDialog open={deleteId !== null} onOpenChange={(open) => !open && setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this visual?</AlertDialogTitle>
              <AlertDialogDescription>
                The saved data and styling go with it. This can't be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Keep it</AlertDialogCancel>
              <AlertDialogAction onClick={() => deleteId && remove.mutate(deleteId)}>
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AppShell>
  );
}
